import { computed, inject, LOCALE_ID } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { enUS } from 'date-fns/locale';
import { DATE_PICKER_LOCALE } from '@shared-ui-kit';

import { CoreService } from '../services/core.service';

// Locales for Angular pipes based on LOCALE_ID
import { registerLocaleData } from '@angular/common';
import localeBg from '@angular/common/locales/bg';
import localeCs from '@angular/common/locales/cs';
import localeDa from '@angular/common/locales/da';
import localeDe from '@angular/common/locales/de';
import localeEnCa from '@angular/common/locales/en-CA';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeEt from '@angular/common/locales/et';
import localeFi from '@angular/common/locales/fi';
import localeFr from '@angular/common/locales/fr';
import localeFrCa from '@angular/common/locales/fr-CA';
import localeHu from '@angular/common/locales/hu';
import localeIt from '@angular/common/locales/it';
import localeLt from '@angular/common/locales/lt';
import localeLv from '@angular/common/locales/lv';
import localeNb from '@angular/common/locales/nb';
import localeNl from '@angular/common/locales/nl';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
// Angular's `pt` is Brazilian Portuguese; `pt-PT` is the European variant.
import localePtPt from '@angular/common/locales/pt-PT';
import localeRo from '@angular/common/locales/ro';
import localeRu from '@angular/common/locales/ru';
import localeSk from '@angular/common/locales/sk';
import localeSv from '@angular/common/locales/sv';
import localeUk from '@angular/common/locales/uk';

registerLocaleData(localeEn);
registerLocaleData(localeEnGb);
registerLocaleData(localeEnCa);
registerLocaleData(localeUk);
registerLocaleData(localeRu);
registerLocaleData(localeFr);
registerLocaleData(localeFrCa);
registerLocaleData(localeDe);
registerLocaleData(localeEs);
registerLocaleData(localePt);
registerLocaleData(localePtPt);
registerLocaleData(localeIt);
registerLocaleData(localePl);
registerLocaleData(localeNl);
registerLocaleData(localeRo);
registerLocaleData(localeCs);
registerLocaleData(localeSk);
registerLocaleData(localeSv);
registerLocaleData(localeDa);
registerLocaleData(localeFi);
registerLocaleData(localeNb);
registerLocaleData(localeHu);
registerLocaleData(localeLt);
registerLocaleData(localeLv);
registerLocaleData(localeEt);
registerLocaleData(localeBg);

/**
 * `LOCALE_ID` drives the relative-date and time pipes, and nothing else — the date
 * pickers take their format from the user's language (`CoreService.getUserLocale()`),
 * not from the browser.
 */
export const provideLocaleId = () => {
  return {
    provide: LOCALE_ID,
    useFactory: () => window.navigator.languages?.[0] || window.navigator.language || 'en-US',
  };
};

/**
 * `MAT_DATE_LOCALE` defaults to `LOCALE_ID`, i.e. a *string*, while `DateFnsAdapter`
 * expects a date-fns `Locale` object — and `provideDateFnsAdapter()` does not provide
 * one. Without this every calendar depends on `DateLocaleService` having run first.
 * `DateLocaleService` still owns the live value; this is the floor under it.
 */
export const provideMatDateLocale = () => ({
  provide: MAT_DATE_LOCALE,
  useValue: enUS,
});

/**
 * Points every picker at the user's language, so no template has to pass `[locale]`.
 * A `computed`: switching language re-lays the masks already on screen.
 */
export const provideDatePickerLocale = () => ({
  provide: DATE_PICKER_LOCALE,
  useFactory: () => {
    const coreService = inject(CoreService);
    return computed(() => coreService.getUserLocale());
  },
});
